import {
    AuthLoginParamType,
    AuthServiceTokens,
    TokenType,
} from 'flow/authservice/authtypes';
import { RegionDataType } from 'flow/types/region-lookup/UserRegion';
import {
    UaePassLoginData,
    UaePassTokensData,
    UaePassWindowCloseReason,
} from 'flow/types/UaePassLogin';
import isElectron from 'is-electron';

declare global {
    interface Window {
        require: NodeJS.Require;
    }
}
export interface CompanionDevice {
    deviceId: string;
    name: string;
    platform: string;
    version: string;
}

export interface ElectronAPIProps {
    authServiceLogin: (authParams: AuthLoginParamType) => Promise<void>;
    authServiceLogout: () => Promise<void>;
    onAuthServiceLoginSuccess: (callback: (tokens: TokenType) => void) => void;
    onAuthServiceLogoutComplete: (callback: () => void) => void;
    removeAuthServiceLoginListener: () => void;
    removeAuthServiceLogoutListener: () => void;
    getCompanionDevice: () => Promise<CompanionDevice | null>;
    setCompanionDeviceName: (name: string) => Promise<CompanionDevice | null>;
    saveUseAsCompanion: (useAsCompanion: boolean) => void;
    revealWindow: () => void;
    saveUserRegionData: (regionData: RegionDataType) => void;
    getUserRegionData: () => Promise<RegionDataType>;
    removeUserRegionData: () => void;
    uaePassLogin: (uaePassData: UaePassLoginData) => Promise<void>;
    uaePassLogout: (uaePassData: {
        uaePassLogoutUrl: string;
    }) => Promise<boolean>;
    uaePassClose: () => void;
    onUaePassTokens: (callback: (data: UaePassTokensData) => void) => void;
    removeUaePassTokensListener: () => void;
    onUaePassWindowClosed: (
        callback: (reason: UaePassWindowCloseReason) => void,
    ) => void;
    removeUaePassWindowClosedListener: () => void;
}

const getElectronAPI = (): ElectronAPIProps | null => {
    const ipcRenderer =
        window && window.require
            ? window.require('electron').ipcRenderer
            : undefined;
    if (!isElectron() || !ipcRenderer) {
        return null;
    }

    return {
        authServiceLogin: (authParams: AuthLoginParamType) =>
            ipcRenderer.invoke('authservice-login', authParams),
        authServiceLogout: () => ipcRenderer.invoke('authservice-logout'),
        onAuthServiceLoginSuccess: (callback: (tokens: TokenType) => void) =>
            ipcRenderer.on(
                'authservice-tokens',
                (
                    _event: Electron.IpcRendererEvent,
                    tokens: AuthServiceTokens,
                ) => callback({ authorizationCode: tokens?.authorizationCode }),
            ),
        onAuthServiceLogoutComplete: (callback: () => void) =>
            ipcRenderer.on('authservice-logout-complete', () => callback()),
        removeAuthServiceLoginListener: () => {
            ipcRenderer.removeAllListeners('authservice-tokens');
        },
        removeAuthServiceLogoutListener: () => {
            ipcRenderer.removeAllListeners('authservice-logout-complete');
        },
        getCompanionDevice: async (): Promise<CompanionDevice | null> => {
            const device = await ipcRenderer.invoke('get-companion-device');
            return device?.deviceId ? device : null;
        },
        setCompanionDeviceName: async (
            name: string,
        ): Promise<CompanionDevice | null> => {
            const device = await ipcRenderer.invoke(
                'set-companion-device-name',
                name,
            );
            return device?.deviceId ? device : null;
        },
        // Write-only mirror for the main process to read at startup; localStorage stays ours.
        saveUseAsCompanion: (useAsCompanion: boolean) =>
            ipcRenderer.send('set-use-as-companion', useAsCompanion),
        revealWindow: () => ipcRenderer.send('reveal-window'),
        saveUserRegionData: (regionData: RegionDataType) =>
            ipcRenderer.send('set-store-data', 'userRegionData', regionData),
        getUserRegionData: async (): Promise<RegionDataType> => {
            const data = await ipcRenderer.invoke(
                'get-store-data',
                'userRegionData',
            );
            return data as RegionDataType;
        },
        removeUserRegionData: () =>
            ipcRenderer.send('remove-store-data', 'userRegionData'),
        uaePassLogin: (uaePassData: UaePassLoginData) =>
            ipcRenderer.invoke('uaepass-login', uaePassData),
        uaePassLogout: (uaePassData: { uaePassLogoutUrl: string }) =>
            ipcRenderer.invoke('uaepass-logout', uaePassData),
        uaePassClose: () => ipcRenderer.send('uaepass-close'),
        onUaePassTokens: (callback: (data: UaePassTokensData) => void) =>
            ipcRenderer.on(
                'uaepass-tokens',
                (_event: Electron.IpcRendererEvent, data: UaePassTokensData) =>
                    callback(data),
            ),
        removeUaePassTokensListener: () =>
            ipcRenderer.removeAllListeners('uaepass-tokens'),
        onUaePassWindowClosed: (
            callback: (reason: UaePassWindowCloseReason) => void,
        ) =>
            ipcRenderer.on(
                'uaepass-window-closed',
                (
                    _event: Electron.IpcRendererEvent,
                    data: { reason: UaePassWindowCloseReason },
                ) => callback(data.reason),
            ),
        removeUaePassWindowClosedListener: () =>
            ipcRenderer.removeAllListeners('uaepass-window-closed'),
    };
};

export { isElectron, getElectronAPI };
